import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons/faCircleCheck';
import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons/faTriangleExclamation';
import { faXmark } from '@fortawesome/pro-solid-svg-icons/faXmark';
import { getLang } from 'shared/store/selectors/lang.selector';
import { formatNumber } from 'shared/utils';
import { IconButton } from 'src/components/design-system';
import { RESULT_VARIANTS } from '../helpers/upload.types';
import type { ImportResult } from '../helpers/upload.types';
import {
  formatRelativeTime,
  formatResultTitle,
  resolveResultVariant,
} from '../helpers/upload.utils';
import { IssuesTable } from './issues-table';
import styles from './import-result-card.module.scss';

const RELATIVE_TIME_REFRESH_MS = 30_000;
const HISTORY_VISIBLE_ROWS = 8;

type ImportResultCardProps = {
  result: ImportResult;
  onDismiss: (id: string) => void;
};

export const ImportResultCard = ({ result, onDismiss }: ImportResultCardProps) => {
  const lang = useSelector(getLang('SETTINGS'));
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), RELATIVE_TIME_REFRESH_MS);
    return () => clearInterval(timer);
  }, []);

  const variant = resolveResultVariant(result);
  const isPartial = variant === RESULT_VARIANTS.PARTIAL;

  const relative = formatRelativeTime(result.completedAt, now);
  const relativeLabel =
    relative.count === undefined
      ? lang[relative.key]
      : lang[relative.key].replace('{count}', formatNumber(relative.count));

  const meta = (result.tabsSkipped > 0 ? lang.SHIFTS_RESULT_META_SKIPPED : lang.SHIFTS_RESULT_META)
    .replace('{file}', result.fileName)
    .replace('{time}', relativeLabel)
    .replace('{imported}', formatNumber(result.tabsImported))
    .replace('{total}', formatNumber(result.tabsTotal))
    .replace('{skipped}', formatNumber(result.tabsSkipped));

  return (
    <div className={classNames(styles.root, isPartial ? styles.partial : styles.success)}>
      <div className={styles.header}>
        <FontAwesomeIcon
          icon={isPartial ? faTriangleExclamation : faCircleCheck}
          className={styles.icon}
        />
        <div className={styles.body}>
          <span className={styles.title}>{formatResultTitle(result, variant, lang)}</span>
          <span className={styles.meta} title={new Date(result.completedAt).toLocaleString()}>
            {meta}
          </span>
        </div>
        <IconButton label={lang.SHIFTS_DISMISS} onClick={() => onDismiss(result.id)}>
          <FontAwesomeIcon icon={faXmark} />
        </IconButton>
      </div>

      {isPartial && result.issues.length > 0 && (
        <IssuesTable issues={result.issues} showSheetColumn maxRows={HISTORY_VISIBLE_ROWS} />
      )}
    </div>
  );
};
